import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import { Table, Modal } from "antd";
import {
  getFstUsersList,
  changeFstUserPassword,
  inactivateFstUser,
} from "../../../apis/fstUsersApi";
import { changeFstPasswordSchema } from "../../../Validations/fstUsersValidation";

const formatName = (record) => {
  if (record.name) return record.name;
  const parts = [record.firstName, record.middleName, record.lastName].filter(
    Boolean,
  );
  return parts.length ? parts.join(" ") : "--";
};

const isActiveFstUser = (record) => {
  const status = (record?.status || "").toString().toUpperCase();
  return status !== "I";
};

const formatStatus = (record) => {
  const status = (record?.status || "").toString().toUpperCase();
  if (status === "I") return "Inactive";
  if (status === "A") return "Active";
  return record?.status || "--";
};

const formatMobile = (record) => {
  const calling = record.mobileCountryCalling || "";
  const mobile = record.mobile || "";
  if (!mobile) return "--";
  return calling ? `+${calling} ${mobile}` : mobile;
};

const FSTUsers = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ name: "", email: "" });
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [inactivatingUserId, setInactivatingUserId] = useState(null);

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async (searchData = {}) => {
    setLoading(true);
    try {
      const res = await getFstUsersList(searchData);
      if (res?.success) {
        setList(res.result || []);
      } else {
        setList([]);
      }
    } catch (e) {
      console.log(e);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchList(filters);
  };

  const handleResetSearch = () => {
    const cleared = { name: "", email: "" };
    setFilters(cleared);
    fetchList(cleared);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const openPasswordModal = (record) => {
    setPasswordUser(record);
    setPasswordMessage("");
    passwordFormik.resetForm();
  };

  const closePasswordModal = () => {
    setPasswordUser(null);
    setPasswordMessage("");
    passwordFormik.resetForm();
  };

  const handleInactivateUser = async (record) => {
    if (!record?.id) return;
    const confirmed = window.confirm(
      `Deactivate Viewer ${formatName(record)} (${record.email})?\n\nThis will set the user to inactive, inactivate all active assignments, and clear CRM/GMO fields.`,
    );
    if (!confirmed) return;

    setInactivatingUserId(record.id);
    try {
      const res = await inactivateFstUser(record.id);
      if (res?.success) {
        window.alert(res?.message || "Viewer deactivated successfully.");
        fetchList(filters);
      } else {
        window.alert(res?.message || "Failed to deactivate Viewer.");
      }
    } catch (e) {
      window.alert("Failed to deactivate Viewer.");
    } finally {
      setInactivatingUserId(null);
    }
  };

  const passwordFormik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: changeFstPasswordSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!passwordUser?.id) return;
      setPasswordLoading(true);
      setPasswordMessage("");
      try {
        const res = await changeFstUserPassword(passwordUser.id, values);
        if (res?.success) {
          setPasswordMessage(res?.message || "Password updated successfully");
          passwordFormik.resetForm();
          setTimeout(closePasswordModal, 1500);
        } else {
          setPasswordMessage(res?.message || "Failed to update password");
        }
      } catch (e) {
        setPasswordMessage("Failed to update password");
      } finally {
        setPasswordLoading(false);
      }
    },
  });

  const columns = [
    {
      title: "S.No",
      render: (_text, _record, index) => <span>{index + 1}</span>,
      width: "8%",
    },
    {
      title: "Name",
      render: (_text, record) => <span>{formatName(record)}</span>,
      width: "22%",
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "25%",
    },
    {
      title: "Mobile",
      render: (_text, record) => <span>{formatMobile(record)}</span>,
      width: "18%",
    },
    {
      title: "Status",
      render: (_text, record) => <span>{formatStatus(record)}</span>,
      width: "10%",
    },
    {
      title: "Actions",
      render: (_text, record) => {
        const isInactivating = inactivatingUserId === record.id;
        return (
          <div className="d-flex flex-wrap gap-1">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => openPasswordModal(record)}
            >
              Change Password
            </button>
            {isActiveFstUser(record) && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => handleInactivateUser(record)}
                disabled={isInactivating}
              >
                {isInactivating ? "..." : "Deactivate"}
              </button>
            )}
          </div>
        );
      },
      width: "22%",
    },
  ];

  return (
    <div className="main-wrapper">
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Viewers</h3>
            <Link to="/add-fst-user" className="btn add-new btn-primary">
              Add Viewers
            </Link>
          </div>

          <div className="mb-3 p-3 bg-body rounded shadow-sm">
            <div className="row user-filter-row">
              <div className="d-flex flex-wrap gap-3 search-boxes">
                <div className="col-sm-6">
                  <input
                    type="text"
                    placeholder="Search by Name"
                    className="form-control floating"
                    value={filters.name}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, name: e.target.value }))
                    }
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
                <div className="col-sm-6">
                  <input
                    type="text"
                    placeholder="Search by Email"
                    className="form-control floating"
                    value={filters.email}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, email: e.target.value }))
                    }
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              </div>
              <div className="col-sm-3 col-md-3 mt-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleResetSearch}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : list.length > 0 ? (
            <Table
              columns={columns}
              dataSource={list}
              className="table-striped"
              rowKey={(record) => record.id}
            />
          ) : (
            <div>No Viewers found</div>
          )}
        </div>
      </div>

      <Modal
        title={`Change Password — ${passwordUser ? formatName(passwordUser) : ""}`}
        open={!!passwordUser}
        footer={null}
        onCancel={closePasswordModal}
        destroyOnClose
      >
        <form onSubmit={passwordFormik.handleSubmit}>
          <div className="input-block mb-3">
            <label className="col-form-label">New Password</label>
            <input
              type="password"
              className="form-control"
              name="password"
              value={passwordFormik.values.password}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
            />
            {passwordFormik.touched.password &&
              passwordFormik.errors.password && (
                <div className="text-danger">
                  {passwordFormik.errors.password}
                </div>
              )}
          </div>
          <div className="input-block mb-3">
            <label className="col-form-label">Confirm Password</label>
            <input
              type="password"
              className="form-control"
              name="confirmPassword"
              value={passwordFormik.values.confirmPassword}
              onChange={passwordFormik.handleChange}
              onBlur={passwordFormik.handleBlur}
            />
            {passwordFormik.touched.confirmPassword &&
              passwordFormik.errors.confirmPassword && (
                <div className="text-danger">
                  {passwordFormik.errors.confirmPassword}
                </div>
              )}
          </div>
          {passwordMessage && (
            <p
              className={
                passwordMessage.toLowerCase().includes("success")
                  ? "text-success"
                  : "text-danger"
              }
            >
              {passwordMessage}
            </p>
          )}
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closePasswordModal}
            >
              Cancel
            </button>
            {passwordLoading ? (
              <div className="spinner-border text-primary" role="status" />
            ) : (
              <button type="submit" className="btn btn-primary">
                Update Password
              </button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FSTUsers;
